import {
	Row,
	Col,
	Card,
	Badge,
	Button,
	Image
} from 'react-bootstrap'
import JobTitle from './JobTitle'
import { useRouter } from 'next/router'
import moment from 'moment'
import styles from '../styles/Event.module.scss'
import techTalentStyles from '../styles/TechTalent.module.scss'
import { useTranslation } from 'next-i18next'
import { isTechTalentEvent } from '../helpers/url'

interface JobProps {
  job: any
  showAvatar: boolean
  withBorder?: boolean // making some props optional
  showCreatedDate?: boolean
  query: string
  showApplicationDate?: boolean
  isCompanyDetail?: boolean
  eventId?: string
}

const Job: React.FC<JobProps> = (props: JobProps) => {
	const { t } = useTranslation()
	const router = useRouter()
	const locale: string = router.locale as string

	const slugify = (str: string): string => {
		const a = 'àáâäæãåāăąçćčđďèéêëēėęěğǵḧîïíīįìłḿñńǹňôöòóœøōõőṕŕřßśšşșťțûüùúūǘůűųẃẍÿýžźż·/_,:;'
		const b = 'aaaaaaaaaacccddeeeeeeeegghiiiiiilmnnnnoooooooooprrsssssttuuuuuuuuuwxyyzzz------'
		const p = new RegExp(a.split('').join('|'), 'g')

		return str.toString().toLowerCase()
			.replace(/\s+/g, '-') // Replace spaces with -
			.replace(p, c => b.charAt(a.indexOf(c))) // Replace special characters
			.replace(/&/g, '-and-') // Replace & with 'and'
			.replace(/[^\w\-]+/g, '') // Remove all non-word characters
			.replace(/\-\-+/g, '-') // Replace multiple - with single -
			.replace(/^-+/, '') // Trim - from start of text
			.replace(/-+$/, '') // Trim - from end of text
	}

	const { job } = props

	const returnClassName = (): string => {
		if (props.withBorder === true) return styles.job
		else if (props.withBorder === false) return styles.jobTwo
		else return styles.jobThree // * applied page specific
	}

	const getJobTypeText = (jt: string): string => {
		// TechTalent event mapping
		if (props.eventId && isTechTalentEvent(props.eventId)) {
			const jobTypeMap: { [key: string]: string } = {
				'FT': 'Permanent',
				'CONTRACT': 'Contract',
				'FREELANCE': 'Freelance',
				'TEMPORARY': 'Temporary',
				'INTERN': 'Internship',
				'CLT': 'Company-Led Training',
				'APPRENTICESHIP': 'Apprenticeship'
			}
			return jobTypeMap[jt] || jt
		}
		// Regular event mapping
		return (jt === 'FT' ? 'Full Time' : (jt === 'PT' ? 'Part Time' : 'Contract'))
	}

	return (
		<a key={job.id} href={`/${locale}/job/${slugify(job.title)}-${job.id as string}${(typeof props.query !== 'undefined' && props.query !== '') ? '?' + props.query : ''}`} target='_blank' rel='noreferrer'>
			<Card className={returnClassName()} style={{ cursor: 'pointer' }}>
				<Card.Body style={{ padding: '1.25rem', boxShadow: props.isCompanyDetail ? 'none' : ''}}>
					<Row>
						{props.showAvatar &&
						<Col xs={3}>
							<div style={{ textAlign: 'center', width: '100%' }}>
								{job.Company.avatar !== null ? <Image thumbnail src={job.Company.avatar} style={{ maxWidth: '100%', objectFit: 'cover', border: '1px solid whitesmoke', alignItems: 'center', maxHeight: '150px' }} alt={job.title} /> : <div className={styles.companyAvatar}>{job.Company.name}</div>}
							</div>
						</Col>}
						<Col md={props.showAvatar ? 6 : 9} xs={props.showAvatar ? 9 : 12}>
							{props.eventId && isTechTalentEvent(props.eventId) && (
								<div style={{ marginBottom: '2px', textAlign: 'center' }}>
									<span style={{ fontSize: '14px', color: '#666' }}>{job.Company.name}</span>
								</div>
							)}
							<div style={{ textAlign: props.eventId && isTechTalentEvent(props.eventId) ? 'center' : 'left' }}>
								<JobTitle job={job} className={props.eventId && isTechTalentEvent(props.eventId) ? `${techTalentStyles.color} fw-bold` : ''} />
							</div>
							{props.eventId && isTechTalentEvent(props.eventId) ? (
								<>
									{job.requirements && (
										<div className={styles.requirements}>
											{job.requirements}
										</div>
									)}
								</>
							) : (
								<h3>{job.Company.name}</h3>
							)}
							<div className={props.eventId && isTechTalentEvent(props.eventId) ? techTalentStyles.color : ''} style={{ marginTop: '5px' }}>
								{job.JobSalaries.map((js: any, i: number) => {
									return (js.min > 0 || js.max > 0) ? <span style={{ display: 'block' }} key={i}><small>{getJobTypeText(js.jobType)}</small> : <span style={{ fontWeight: 600 }}>{js.min > 0 ? `$ ${js.min as string}` : ''} {js.min > 0 && js.max > 0 ? ' - ' : ''} {js.max > 0 ? `$ ${js.max as string}` : ''}</span> <small> / {js.salaryType === null ? 'PER MONTH' : js.salaryType}</small></span> : ''
								})}
							</div>
							{props.eventId && isTechTalentEvent(props.eventId) && job.vacancies && (
								<div style={{ marginTop: '5px', marginBottom: '10px' }}>
									<small style={{ color: '#666', fontWeight: 'bold' }}>
										{job.vacancies} {job.vacancies === 1 ? 'vacancy' : 'vacancies'}
									</small>
								</div>
							)}
							<div className={styles.badges}>
								{(typeof job.JobHashTags !== 'undefined' && job.JobHashTags !== null) &&
                  job.JobHashTags.length > 0 && job.JobHashTags.map((ele, index) => {
									const { Hashtag } = ele
									return (
										<Badge
											key={index}
											pill
											bg='secondary'
											style={{ backgroundColor: Hashtag.color, marginTop: '10px', marginRight: '5px' }}
										>
                      #{Hashtag.hashtag}
										</Badge>
									)
								})}
								{job.JobSalaries.map((js: any, i: number) => {
									return (
										<Badge key={i} pill bg='info' style={{ marginRight: '5px' }}>
											{getJobTypeText(js.jobType)}
										</Badge>
									)
								})}
								{job.Category && job.Category.name && (
									<Badge pill bg='secondary' style={{ marginRight: '5px' }}>
										{job.Category.name}
									</Badge>
								)}
								{/* {props.eventId && isTechTalentEvent(props.eventId) && (
									<Badge pill bg='secondary' style={{ marginRight: '5px' }}>
										Professional
									</Badge>
								)} */}
								{props.eventId && isTechTalentEvent(props.eventId) && job.Occupation && job.Occupation.name && (
									<Badge pill bg='secondary' style={{ marginRight: '5px' }}>
										{job.Occupation.name}
									</Badge>
								)}
							</div>
						</Col>
						<Col md={3} className={styles.showDetailsContainer}>
							{/* {props.showCreatedDate && <span className='text-black-50'>{moment(job.jobPostStart).fromNow()}</span>} */}
							{props.showApplicationDate && <span className='text-black-50'>Applied {moment(job.JobApplicants[0].createdAt).fromNow()}</span>}
							<Button className={`text-white ${props.eventId && isTechTalentEvent(props.eventId) ? techTalentStyles.primaryBtn : ''}`} variant={props.eventId && isTechTalentEvent(props.eventId) ? undefined : 'primary'}>{t('job_list.show_details')}</Button>
						</Col>
					</Row>
				</Card.Body>
			</Card>
		</a>
	)
}

export default Job
